import {
	ConnectedStandardSolanaWallet,
	useWallets,
} from '@privy-io/react-auth/solana';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePrivy, User, WalletWithMetadata } from '@privy-io/react-auth';
import useWalletContext from '../../useWalletContext';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import { isWalletBlacklistedForEmbeddedWallets } from 'src/constants/wallets';

interface PrivyDetails {
	/** The authenticated Privy user */
	user: User | null;
	/** The list of connected solana wallets, including the EOA wallet if connected, and embedded wallet(s) created */
	wallets: ConnectedStandardSolanaWallet[];
	/** The External Owned Account (EOA) wallet if connected */
	eoaWallet: ConnectedStandardSolanaWallet | undefined;
	/** The embedded wallet if created. The first embedded wallet in the wallets array is the designated embedded wallet.  */
	embeddedWallet: ConnectedStandardSolanaWallet | undefined;
	/** The Solana wallet authority for the Privy user */
	solanaWalletAuthorityForPrivyUser: string | undefined;
}

/**
 * There are 2 components in how we set Privy up: external wallets and embedded wallets.
 * - External wallets (EOA - externally owned account) are usually browser extension/mobile app wallets (e.g. Phantom, Solflare)
 * - Embedded wallets are Privy wallets that are created when a user uses their external wallet to authenticate and sign in to Privy.
 *
 * In general, if a user is an External wallet user, we will create a Privy embedded wallet, assign this embedded wallet as a delegate
 * to the user's subaccounts, and thus allowing sign-less transactions by having the UI control the delegate.
 */
export const usePrivyEmbeddedWallet = (): PrivyDetails => {
	const { user } = usePrivy();
	const { wallets } = useWallets();
	const walletContext = useWalletContext();
	const currentUserAccount = useCurrentUserAccount();

	const [eoaWallet, _setEoaWallet] = useState<
		ConnectedStandardSolanaWallet | undefined
	>(undefined);

	const [embeddedWallet, _setEmbeddedWallet] = useState<
		ConnectedStandardSolanaWallet | undefined
	>(undefined);

	const solanaWalletAuthorityForPrivyUser = useMemo(
		() =>
			user?.linkedAccounts?.find(
				(account): account is WalletWithMetadata =>
					(account as WalletWithMetadata).connectorType !== 'embedded' &&
					(account as WalletWithMetadata).type === 'wallet'
			)?.address,
		[user?.linkedAccounts]
	);

	/**
	 * We assume if a wallet has the same address and name as the previous EOA wallet, then it is the same wallet,
	 * to maintain the wallet reference.
	 */
	const handleSetEoaWallet = useCallback(
		(eoaWallet: ConnectedStandardSolanaWallet | undefined) => {
			_setEoaWallet((prev) => {
				if (!prev || !eoaWallet) return eoaWallet;

				if (
					prev.address === eoaWallet.address &&
					prev.standardWallet.name === eoaWallet.standardWallet.name
				)
					return prev;

				return eoaWallet;
			});
		},
		[_setEoaWallet]
	);

	const handleSetEmbeddedWallet = useCallback(
		(embeddedWallet: ConnectedStandardSolanaWallet | undefined) => {
			_setEmbeddedWallet((prev) => {
				if (!prev || !embeddedWallet) return embeddedWallet;

				if (
					prev.address === embeddedWallet.address &&
					prev.standardWallet.name === embeddedWallet.standardWallet.name
				)
					return prev;

				return embeddedWallet;
			});
		},
		[_setEmbeddedWallet]
	);

	/**
	 * Syncs the EOA wallet state between the Solana Wallet Adapter and Privy.
	 * Derives the Privy EOA wallet from the Solana Wallet Adapter.
	 */
	useEffect(() => {
		const walletAdapterName = walletContext.wallet?.adapter?.name;

		// there is a bug where Privy can detect EOA wallets, even though they are not connected to the wallet adapter OR Privy. hence we assume there is no EOA if the wallet adapter is undefined
		if (!walletAdapterName) {
			handleSetEoaWallet(undefined);
			return;
		}

		const eoaWallets = wallets.filter(
			(wallet) => wallet.standardWallet.name !== 'Privy'
		);
		if (eoaWallets.length === 0) {
			handleSetEoaWallet(undefined);
			return;
		}

		if (eoaWallets.length > 1) {
			const adapterWallet = eoaWallets.find(
				(wallet) => wallet.standardWallet.name === walletAdapterName
			);

			if (adapterWallet) {
				handleSetEoaWallet(adapterWallet);
			} else {
				handleSetEoaWallet(eoaWallets[0]);
			}
			return;
		}

		handleSetEoaWallet(eoaWallets[0]);
	}, [wallets, walletContext.wallet?.adapter?.name, handleSetEoaWallet]);

	/**
	 * Syncs the embedded wallet state from Privy.
	 * Disabled when a blacklisted wallet is connected.
	 */
	useEffect(() => {
		const walletAdapterName = walletContext.wallet?.adapter?.name;
		const isBlacklistedWalletAdapter =
			isWalletBlacklistedForEmbeddedWallets(walletAdapterName);

		if (isBlacklistedWalletAdapter) {
			handleSetEmbeddedWallet(undefined);
			return;
		}

		const possibleEmbeddedWallets = wallets.filter(
			(wallet) => wallet.standardWallet.name === 'Privy'
		);

		// due to race conditions in creating a Privy embedded wallet, sometimes more than one embedded wallet is created.
		// in this case, if the wallet is already delegated to one of the embedded wallets, use that one.
		const delegatedEmbeddedWallet = possibleEmbeddedWallets.find(
			(embeddedWallet) =>
				embeddedWallet.address === currentUserAccount?.delegate.toString()
		);

		if (delegatedEmbeddedWallet) {
			handleSetEmbeddedWallet(delegatedEmbeddedWallet);
			return;
		}

		handleSetEmbeddedWallet(possibleEmbeddedWallets[0]);
	}, [
		wallets,
		currentUserAccount?.delegate?.toString(),
		handleSetEmbeddedWallet,
		walletContext.wallet?.adapter?.name,
	]);

	return {
		user,
		wallets,
		eoaWallet,
		embeddedWallet,
		solanaWalletAuthorityForPrivyUser,
	};
};
